import { useState, useMemo } from "react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Card } from "../../../components/ui/card";
import SearchBar from "../../../components/ui/SearchBar";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../../components/ui/select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faShoppingCart,
  faUserTie,
  faBoxes,
  faBox,
  faPlus,
  faTimes,
  faTrash,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import { toastError, toastSuccess } from "../../../utils/toast";

export const AddSaleModal = ({
  isOpen,
  onClose,
  onAdd,
  sellers = [],
  stockData = [],
}) => {
  const [form, setForm] = useState({
    sellerId: "",
    productTypeId: "",
    productCategoryId: "",
  });
  const [productQuantities, setProductQuantities] = useState({});
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [saleItems, setSaleItems] = useState([]);
  const [error, setError] = useState("");
  const [tableError, setTableError] = useState("");
  const [errorProductId, setErrorProductId] = useState(null);
  const [productSearchTerm, setProductSearchTerm] = useState("");
  const [customSellingPrices, setCustomSellingPrices] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Filter ONLY active sellers
  const activeSellers = useMemo(() => {
    return (Array.isArray(sellers) ? sellers : []).filter(
      (seller) => seller?.status?.toLowerCase() === "active",
    );
  }, [sellers]);

  const selectedSeller = useMemo(
    () =>
      activeSellers.find(
        (seller) =>
          String(
            seller?.shop_saller_id || seller?.shopSellerId || seller?.id,
          ) === form.sellerId,
      ) || null,
    [activeSellers, form.sellerId],
  );

  // Filter ONLY active stock with remaining items
  const activeStockData = useMemo(() => {
    return (Array.isArray(stockData) ? stockData : []).filter((stock) => {
      const isActive = stock?.status?.toLowerCase() === "active";
      const remainingItems = Number(
        stock?.summary?.remainingItems ??
          stock?.remainingItems ??
          stock?.remaining_items ??
          0,
      );
      const hasRemaining = remainingItems > 0;
      return isActive && hasRemaining;
    });
  }, [stockData]);

  // The stock list is the source of truth for the sale picker.  The API may
  // return product/category data nested under `product` or flattened, so this
  // normalizes both shapes without additional product API calls.
  const enrichedStockData = useMemo(() => {
    return activeStockData.map((stock) => {
      const product = stock.product || stock;
      const category = product.product_category || product.category || {};
      const type = category.product_type || product.product_type || {};
      const productTypeName = typeof type === "string" ? type : type.name;
      const productCategoryName =
        category.category_name || product.category_name;
      const productTypeId =
        type.productTypeId ||
        type.product_type_id ||
        product.product_type_id ||
        productTypeName;
      const productCategoryId =
        category.productCategoryId ||
        category.product_category_id ||
        product.product_category_id ||
        productCategoryName;
      return {
        ...stock,
        stockId: stock.stockId || stock.id,
        productName:
          product.product_name || stock.productName || "Unknown product",
        productTypeId: String(productTypeId || ""),
        productTypeName: productTypeName || "Uncategorized",
        productCategoryId: String(productCategoryId || ""),
        productCategoryName: productCategoryName || "Uncategorized",
        packageType: product.package_type || stock.packageType || "N/A",
        noOfItems: product.no_of_items || stock.noOfItems || 1,
        shopId: stock.shopId || stock.shop_id,
        shopName: stock.shop?.shop_name || stock.shopName,
      };
    });
  }, [activeStockData]);

  // Get available product types from enriched stock
  const availableProductTypes = useMemo(() => {
    const typeIds = new Set();
    const result = [];

    enrichedStockData.forEach((stock) => {
      if (stock.productTypeId && !typeIds.has(stock.productTypeId)) {
        typeIds.add(stock.productTypeId);
        result.push({ id: stock.productTypeId, name: stock.productTypeName });
      }
    });

    return result;
  }, [enrichedStockData]);

  // Get categories based on selected product type
  const getCategoriesForType = (typeId) => {
    if (!typeId) return [];

    const categoryIds = new Set();
    const result = [];

    enrichedStockData.forEach((stock) => {
      if (
        stock.productTypeId === typeId &&
        stock.productCategoryId &&
        !categoryIds.has(stock.productCategoryId)
      ) {
        categoryIds.add(stock.productCategoryId);
        result.push({
          id: stock.productCategoryId,
          name: stock.productCategoryName,
        });
      }
    });

    return result;
  };

  // Get products based on selected type and category
  const getProductsForCategory = (typeId, categoryId) => {
    if (!typeId || !categoryId) return [];

    return enrichedStockData.filter(
      (stock) =>
        stock.productTypeId === typeId &&
        stock.productCategoryId === categoryId,
    );
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "0 TSH";
    return (
      new Intl.NumberFormat("sw-TZ", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount) + " TSH"
    );
  };

  const handleTypeChange = (value) => {
    setForm({ ...form, productTypeId: value, productCategoryId: "" });
    const categories = getCategoriesForType(value);
    setFilteredCategories(categories);
    setFilteredProducts([]);
    setProductQuantities({});
    setCustomSellingPrices({});
    setError("");
    setTableError("");
    setErrorProductId(null);
    setProductSearchTerm("");
  };

  const handleCategoryChange = (value) => {
    setForm({ ...form, productCategoryId: value });
    const products = getProductsForCategory(form.productTypeId, value);
    setFilteredProducts(products);

    const initialQuantities = {};
    const initialPrices = {};
    products.forEach((p) => {
      if (p?.stockId) {
        initialQuantities[p.stockId] = "";
        initialPrices[p.stockId] = "";
      }
    });
    setProductQuantities(initialQuantities);
    setCustomSellingPrices(initialPrices);
    setError("");
    setTableError("");
    setErrorProductId(null);
    setProductSearchTerm("");
  };

  const handleQuantityChange = (productId, value) => {
    if (!productId) return;
    setProductQuantities({
      ...productQuantities,
      [productId]: value,
    });
    setError("");
    setTableError("");
    setErrorProductId(null);
  };

  const handleSellingPriceChange = (productId, value) => {
    if (!productId) return;
    setCustomSellingPrices({
      ...customSellingPrices,
      [productId]: value,
    });
    setError("");
    setTableError("");
    setErrorProductId(null);
  };

  const addSaleItem = (product) => {
    if (!product) return;

    if (!form.sellerId) {
      setError("Please select a seller first before adding products.");
      toastError("Please select a seller first before adding products.");
      return;
    }

    const seller = selectedSeller;
    if (!seller) {
      setError("Selected seller not found. Please select a valid seller.");
      toastError("Selected seller not found. Please select a valid seller.");
      return;
    }

    const quantity = parseInt(productQuantities[product.stockId]);
    const actualSellingPrice = parseFloat(customSellingPrices[product.stockId]);
    const remainingItems = Number(
      product.summary?.remainingItems ??
        product.remainingItems ??
        product.remaining_items ??
        0,
    );

    if (isNaN(quantity) || quantity < 0) {
      const msg = `Please enter a valid number of unsold items for ${product.productName}`;
      setError(msg);
      toastError(msg);
      return;
    }

    if (isNaN(actualSellingPrice) || actualSellingPrice <= 0) {
      const msg = `Please enter the actual sale revenue for ${product.productName}`;
      setError(msg);
      toastError(msg);
      return;
    }

    // Guard 1: no stock left
    if (remainingItems <= 0) {
      const errorMsg = `No stock remaining for ${product.productName}.`;
      setTableError(errorMsg);
      setErrorProductId(product.stockId);
      setError("");
      toastError(errorMsg);
      return;
    }

    // Guard 2: entered more than remaining
    if (quantity > remainingItems) {
      const errorMsg = `Quantity cannot exceed available stock (${remainingItems} items remaining) for ${product.productName}`;
      setTableError(errorMsg);
      setErrorProductId(product.stockId);
      setError("");
      toastError(errorMsg);
      return;
    }

    // Guard 3: entered exactly the remaining — nothing sold
    if (quantity === remainingItems) {
      const errorMsg = `You must sell at least 1 item. Remaining is ${remainingItems}. Enter a number less than ${remainingItems} (or 0 to sell all).`;
      setTableError(errorMsg);
      setErrorProductId(product.stockId);
      setError("");
      toastError(errorMsg);
      return;
    }

    const existingItem = saleItems.find(
      (item) => item?.stockId === product.stockId,
    );
    if (existingItem) {
      const msg = `${product.productName} is already added. Please edit it in the list below.`;
      setError(msg);
      toastError(msg);
      return;
    }

    const totalItems = Number(
      product.summary?.totalItems ??
        product.totalItems ??
        product.total_items ??
        0,
    );
    const itemsSold = Math.max(totalItems - quantity, 1);

    const newItem = {
      id: Date.now(),
      sellerId: form.sellerId,
      sellerRegNo: seller.username || "N/A",
      sellerName: seller.user?.full_name || seller.username || "Unknown",
      sellerEmail: seller.user?.email || "N/A",
      sellerPhone: seller.user?.phone || "N/A",
      stockId: product.stockId,
      productName: product.productName,
      productType: product.productTypeName,
      productCategory: product.productCategoryName,
      packageType: product.packageType || "N/A",
      noOfItems: product.noOfItems || 1,
      dozenAmount: product.dozen_amount || 0,
      totalItems: totalItems,
      remainingItems: remainingItems,
      purchasePrice: parseFloat(product.purchase_price) || 0,
      sellingPricePerItem: actualSellingPrice / itemsSold,
      sellingPrice: actualSellingPrice,
      originalSellingPrice: parseFloat(product.selling_price) || 0,
      purchaseDate: product.purchase_date || new Date().toISOString(),
      noOfUnSold: quantity,
      status: "ACTIVE",
      shopId: product.shopId,
      shopName: product.shopName || "N/A",
    };

    setSaleItems([...saleItems, newItem]);

    setProductQuantities({
      ...productQuantities,
      [product.stockId]: "",
    });
    setCustomSellingPrices({
      ...customSellingPrices,
      [product.stockId]: "",
    });

    setFilteredProducts((prevProducts) =>
      prevProducts.filter((p) => p?.stockId !== product.stockId),
    );

    setError("");
    setTableError("");
    setErrorProductId(null);

    toastSuccess(`${product.productName} added to sale!`);
  };

  const removeSaleItem = (id) => {
    if (!id) return;
    const removedItem = saleItems.find((item) => item?.id === id);

    setSaleItems(saleItems.filter((item) => item?.id !== id));

    if (removedItem && form.productTypeId && form.productCategoryId) {
      const product = enrichedStockData.find(
        (s) => s?.stockId === removedItem.stockId,
      );
      if (product) {
        setFilteredProducts((prev) => [...prev, product]);
        setCustomSellingPrices({
          ...customSellingPrices,
          [product.stockId]: "",
        });
      }
    }

    setError("");
    setTableError("");
    setErrorProductId(null);
  };

  const handleSubmitAll = async () => {
    if (saleItems.length === 0) {
      setError("Please add at least one product to sell.");
      toastError("Please add at least one product to sell.");
      return;
    }

    setIsSubmitting(true);
    try {
      await onAdd(saleItems);
    } catch (submitError) {
      const message =
        submitError?.message || "Could not create the sale. Please try again.";
      setError(message);
      toastError(message);
      return;
    } finally {
      setIsSubmitting(false);
    }

    setSaleItems([]);
    setProductQuantities({});
    setCustomSellingPrices({});
    setFilteredProducts([]);
    setFilteredCategories([]);
    setForm({
      sellerId: "",
      productTypeId: "",
      productCategoryId: "",
    });
    setError("");
    setTableError("");
    setErrorProductId(null);
    setProductSearchTerm("");

    toastSuccess(`${saleItems.length} sale(s) added successfully!`);
    onClose();
  };

  const totalRevenue = saleItems.reduce((sum, item) => {
    if (!item) return sum;
    return sum + (item.sellingPrice || 0);
  }, 0);

  const totalProfit = saleItems.reduce((sum, item) => {
    if (!item) return sum;
    const costPerItem = item.purchasePrice / item.totalItems;
    const itemsSold = item.totalItems - item.noOfUnSold;
    return sum + ((item.sellingPricePerItem || 0) - costPerItem) * itemsSold;
  }, 0);

  const filteredProductsBySearch = useMemo(() => {
    if (!productSearchTerm.trim()) return filteredProducts || [];
    const search = productSearchTerm.toLowerCase().trim();
    return (filteredProducts || []).filter((product) =>
      product?.productName?.toLowerCase().includes(search),
    );
  }, [filteredProducts, productSearchTerm]);

  if (!isOpen) return null;

  // Check if there are any products available
  if (enrichedStockData.length === 0) {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-lg shadow-xl p-8">
          <div className="flex flex-col items-center justify-center gap-4 text-center">
            <div className="w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center">
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="h-8 w-8 text-yellow-600"
              />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              No Products Available
            </h3>
            <p className="text-sm text-gray-600">
              There are no active products with remaining stock in this shop.
              Please add stock or activate products before creating a sale.
            </p>
            <Button
              onClick={onClose}
              className="mt-2 bg-blue-800 hover:bg-blue-900 text-white"
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 flex items-start justify-center p-4">
      <div className="w-full max-w-6xl mt-8 bg-white rounded-lg shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex flex-col gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <FontAwesomeIcon
                  icon={faShoppingCart}
                  className="text-blue-800 h-4 w-4"
                />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Add new sale
                </h3>
                <p className="text-sm text-gray-600">
                  Select seller, choose products, and add quantities
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={onClose}
              className="text-gray-600 hover:text-gray-800"
            >
              <FontAwesomeIcon icon={faTimes} className="h-4 w-4" />
            </Button>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="h-4 w-4 text-red-500"
              />
              <span>{error}</span>
              <button
                onClick={() => setError("")}
                className="ml-auto text-red-500 hover:text-red-700"
              >
                <FontAwesomeIcon icon={faTimes} className="h-3 w-3" />
              </button>
            </div>
          )}

          <Card className="p-4 bg-white border border-gray-200">
            <div className="flex flex-col gap-3">
              <Label className="text-gray-700 font-medium flex items-center gap-2">
                <FontAwesomeIcon
                  icon={faUserTie}
                  className="text-blue-800 h-3.5 w-3.5"
                />
                Select Seller<span className="text-red-700">*</span>
              </Label>
              <Select
                value={form.sellerId}
                onValueChange={(value) => {
                  setForm({ ...form, sellerId: value });
                  setError("");
                  setTableError("");
                  setErrorProductId(null);
                }}
              >
                <SelectTrigger className="h-10 text-sm border-gray-300">
                  <SelectValue placeholder="Choose a seller" />
                </SelectTrigger>
                <SelectContent>
                  {activeSellers.length > 0 ? (
                    activeSellers.map((seller) => {
                      const sellerId = String(
                        seller?.shop_saller_id ||
                          seller?.shopSellerId ||
                          seller?.id ||
                          "unknown",
                      );
                      return (
                        <SelectItem key={sellerId} value={sellerId}>
                          {seller?.user?.full_name ||
                            seller?.username ||
                            "Unknown Seller"}
                        </SelectItem>
                      );
                    })
                  ) : (
                    <div className="px-3 py-2 text-sm text-gray-600 text-center">
                      No active sellers available for this shop
                    </div>
                  )}
                </SelectContent>
              </Select>
              {selectedSeller && (
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 text-sm text-gray-600 bg-gray-50 p-2 rounded-lg">
                  <span>
                    <strong>Phone:</strong>{" "}
                    {selectedSeller.user?.phone || "N/A"}
                  </span>
                  <span>
                    <strong>Email:</strong>{" "}
                    {selectedSeller.user?.email || "N/A"}
                  </span>
                  <span>
                    <strong>Reg No:</strong> {selectedSeller.username || "N/A"}
                  </span>
                </div>
              )}
            </div>
          </Card>

          <Card className="p-4 bg-white border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <Label className="text-gray-700 font-medium flex items-center gap-2">
                  <FontAwesomeIcon
                    icon={faBoxes}
                    className="text-blue-800 h-3.5 w-3.5"
                  />
                  Product Type<span className="text-red-700">*</span>
                </Label>
                <Select
                  value={form.productTypeId}
                  onValueChange={handleTypeChange}
                >
                  <SelectTrigger className="h-10 text-sm border-gray-300">
                    <SelectValue placeholder="Select product type" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableProductTypes.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-2">
                <Label className="text-gray-700 font-medium flex items-center gap-2">
                  <FontAwesomeIcon
                    icon={faBox}
                    className="text-blue-800 h-3.5 w-3.5"
                  />
                  Product Category<span className="text-red-700">*</span>
                </Label>
                <Select
                  value={form.productCategoryId}
                  onValueChange={handleCategoryChange}
                  disabled={!form.productTypeId}
                >
                  <SelectTrigger className="h-10 text-sm border-gray-300">
                    <SelectValue placeholder="Select product category" />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredCategories.map((category) => (
                      <SelectItem key={category.id} value={category.id}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </Card>

          {filteredProductsBySearch.length > 0 && (
            <Card className="p-4 bg-white border border-gray-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                <h4 className="text-sm font-semibold text-gray-700">
                  Products ({filteredProductsBySearch.length} available)
                </h4>
                <div className="w-full sm:w-64">
                  <SearchBar
                    value={productSearchTerm}
                    onChange={setProductSearchTerm}
                    placeholder="Search products by name..."
                  />
                </div>
              </div>

              {tableError && (
                <div className="flex items-center gap-2 p-3 mb-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  <FontAwesomeIcon
                    icon={faExclamationTriangle}
                    className="h-4 w-4 text-red-500"
                  />
                  <span>{tableError}</span>
                  <button
                    onClick={() => {
                      setTableError("");
                      setErrorProductId(null);
                    }}
                    className="ml-auto text-red-500 hover:text-red-700"
                  >
                    <FontAwesomeIcon icon={faTimes} className="h-3 w-3" />
                  </button>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">
                        Product
                      </th>
                      <th className="text-center py-2 px-3 text-xs font-semibold text-gray-600 hidden md:table-cell">
                        Package
                      </th>
                      <th className="text-center py-2 px-3 text-xs font-semibold text-gray-600 hidden md:table-cell">
                        Total Items
                      </th>
                      <th className="text-center py-2 px-3 text-xs font-semibold text-gray-600 hidden md:table-cell">
                        Remaining
                      </th>
                      <th className="text-center py-2 px-3 text-xs font-semibold text-gray-600">
                        Actual Sale Revenue
                      </th>
                      <th className="text-center py-2 px-3 text-xs font-semibold text-gray-600">
                        No. of Unsold
                      </th>
                      <th className="text-center py-2 px-3 text-xs font-semibold text-gray-600">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredProductsBySearch.map((product) => {
                      const isErrorRow = errorProductId === product?.stockId;
                      const totalItems = Number(
                        product.summary?.totalItems ??
                          product.totalItems ??
                          product.total_items ??
                          0,
                      );
                      const remainingItems = Number(
                        product.summary?.remainingItems ??
                          product.remainingItems ??
                          product.remaining_items ??
                          0,
                      );
                      return (
                        <tr
                          key={product?.stockId}
                          className={`hover:bg-gray-50 transition-colors ${
                            isErrorRow
                              ? "bg-red-50 border-l-4 border-red-500"
                              : ""
                          }`}
                        >
                          <td className="py-2 px-3">
                            <div>
                              <p
                                className={`font-medium text-blue-900 ${
                                  isErrorRow ? "text-red-700" : ""
                                }`}
                              >
                                {product?.productName || "Unknown"}
                              </p>
                              <p className="text-orange-600 font-semibold italic">
                                {product?.productCategoryName || "N/A"}
                              </p>
                              <div className="md:hidden mt-1">
                                <p className="text-gray-800 text-xs">
                                  Package: {product?.packageType || "N/A"}
                                </p>
                                <p className="text-gray-600 font-medium text-xs">
                                  Total: {totalItems} | Remaining:{" "}
                                  {remainingItems}
                                </p>
                              </div>
                              {isErrorRow && (
                                <p className="text-xs text-red-600 mt-1">
                                  <FontAwesomeIcon
                                    icon={faExclamationTriangle}
                                    className="h-3 w-3 mr-1"
                                  />
                                  {tableError}
                                </p>
                              )}
                            </div>
                          </td>
                          <td className="py-2 px-3 text-center text-gray-600 hidden md:table-cell">
                            {product?.packageType || "N/A"}
                          </td>
                          <td className="py-2 px-3 text-center text-gray-600 hidden md:table-cell">
                            {totalItems}
                          </td>
                          <td className="py-2 px-3 text-center text-gray-600 hidden md:table-cell">
                            <span className="font-semibold text-green-600">
                              {remainingItems}
                            </span>
                          </td>
                          <td className="py-2 px-3">
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={
                                customSellingPrices[product?.stockId] || ""
                              }
                              onChange={(e) =>
                                handleSellingPriceChange(
                                  product?.stockId,
                                  e.target.value,
                                )
                              }
                              placeholder="Actual total revenue"
                              className={`w-28 mx-auto text-center text-sm h-8 ${
                                isErrorRow
                                  ? "border-red-500 ring-1 ring-red-500"
                                  : ""
                              }`}
                            />
                          </td>
                          <td className="py-2 px-3">
                            <Input
                              type="number"
                              min="0"
                              max={remainingItems}
                              value={productQuantities[product?.stockId] || ""}
                              onChange={(e) =>
                                handleQuantityChange(
                                  product?.stockId,
                                  e.target.value,
                                )
                              }
                              placeholder="0"
                              className={`w-20 mx-auto text-center text-sm h-8 ${
                                isErrorRow
                                  ? "border-red-500 ring-1 ring-red-500"
                                  : ""
                              }`}
                            />
                          </td>
                          <td className="py-2 px-3 text-center">
                            <Button
                              size="sm"
                              className={`h-8 px-3 text-xs text-white bg-blue-900 hover:bg-blue-800 ${
                                isErrorRow ? "border-red-500" : ""
                              }`}
                              onClick={() => addSaleItem(product)}
                            >
                              <span className="hidden sm:inline">
                                <FontAwesomeIcon
                                  icon={faPlus}
                                  className="h-3 w-3 mr-1"
                                />
                              </span>
                              Add
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {filteredProductsBySearch.length === 0 && productSearchTerm && (
                <div className="text-center py-4 text-gray-600 text-sm">
                  No products found matching "{productSearchTerm}"
                </div>
              )}
            </Card>
          )}

          {saleItems.length > 0 && (
            <Card className="p-4 bg-white border border-gray-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                <h4 className="text-sm font-semibold text-gray-700">
                  Sale Items ({saleItems.length})
                </h4>
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  <span className="text-gray-600">
                    Total Revenue:{" "}
                    <strong className="text-green-600">
                      {formatCurrency(totalRevenue)}
                    </strong>
                  </span>
                  <span className="text-gray-600">
                    Total Profit:{" "}
                    <strong
                      className={`${
                        totalProfit >= 0 ? "text-green-600" : "text-red-600"
                      }`}
                    >
                      {formatCurrency(totalProfit)}
                    </strong>
                  </span>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">
                        Product
                      </th>
                      <th className="text-center py-2 px-3 text-xs font-semibold text-gray-600 hidden md:table-cell">
                        Seller
                      </th>
                      <th className="text-center py-2 px-3 text-xs font-semibold text-gray-600">
                        Selling price
                      </th>
                      <th className="text-center py-2 px-3 text-xs font-semibold text-gray-600">
                        Unsold
                      </th>
                      <th className="text-right py-2 px-3 text-xs font-semibold text-gray-600 hidden sm:table-cell">
                        Revenue
                      </th>
                      <th className="text-center py-2 px-3 text-xs font-semibold text-gray-600">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {saleItems.map((item) => {
                      if (!item) return null;
                      const revenue = item.sellingPrice || 0;

                      return (
                        <tr
                          key={item.id}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="py-2 px-3">
                            <p className="font-medium text-gray-800">
                              {item.productName || "Unknown"}
                            </p>
                            <p className="text-xs text-gray-600 font-medium">
                              {item.productCategory || "N/A"}
                            </p>
                            <p className="text-xs text-gray-500">
                              Remaining: {item.remainingItems}
                            </p>
                          </td>
                          <td className="py-2 px-3 text-center text-gray-600 hidden md:table-cell">
                            {item.sellerName || "Unknown"}
                          </td>
                          <td className="py-2 px-3 text-center font-medium text-gray-700">
                            {formatCurrency(item.sellingPrice)}
                          </td>
                          <td className="py-2 px-3 text-center font-semibold text-gray-700">
                            {item.noOfUnSold}
                          </td>
                          <td className="py-2 px-3 text-right text-green-600 font-medium hidden sm:table-cell">
                            {formatCurrency(revenue)}
                          </td>
                          <td className="py-2 px-3 text-center">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                              onClick={() => removeSaleItem(item.id)}
                            >
                              <FontAwesomeIcon
                                icon={faTrash}
                                className="h-3.5 w-3.5"
                              />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          <div className="flex flex-col sm:flex-row justify-end gap-2 pt-2 border-t border-gray-200">
            <Button
              variant="outline"
              onClick={onClose}
              className="w-full sm:w-auto px-6 border-2 border-orange-700 text-red-600 hover:text-white hover:bg-orange-600 hover:border-none"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitAll}
              disabled={saleItems.length === 0 || isSubmitting}
              className="gap-2 w-full sm:w-auto bg-blue-900 hover:bg-blue-800 text-white px-6 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FontAwesomeIcon icon={faSave} />
              {isSubmitting
                ? "Saving..."
                : `Save All Sales (${saleItems.length})`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddSaleModal;
